/* eslint-disable max-lines */
import { useEffect, useReducer, useState } from 'react'
import {
  CampaignData,
  TestedContentSection,
} from '@phrasee/phrasee-typings/Graphql/interfaces'
import * as Drawer from 'app/IntegrationDrawer'
import {
  addDays,
  addMinutes,
  differenceInSeconds,
  endOfDay,
  format,
  isBefore,
  subMinutes,
} from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import isEqual from 'lodash/isEqual'
import { GET_MID_LOADING } from 'redux/actionTypes'
import { MidObject } from 'workflow/interface'
import helpers from 'workflow/utils/helpers'
import {
  fetchCampaignData,
  getMID,
  loadResultsDetails,
  resetMidList,
} from 'workflow/Workflow.actions'

import Card from 'common/components/Card'
import DatePicker from 'common/components/datePicker'
import FormItem from 'common/components/formItem'
import GuidingText from 'common/components/guidingText'
import Label from 'common/components/label'
import SingleSelect from 'common/components/singleSelect'
import TimePicker from 'common/components/timePicker'
import { env } from 'common/config/env'
import { handlePercentValue } from 'common/helpers/numeric'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { Time as TimeIcon } from 'common/icons'

import AdvancedOptions from './components/advancedOptions/AdvancedOptions'
import {
  confidence,
  highPerformers,
  optionHeadToHeadAndWait,
  singleBest,
} from './components/advancedOptions/constants'
import OptimizationSettings from './components/advancedOptions/OptimizationSettings'
import CopyCard from './components/CopyCard'
import { getTestModeDescription, TestModeAlert } from './components/testMode'
import {
  advancedSettingsReducer,
  initialAdvancedOptionsState,
} from './advancedOptionsReducer'
import {
  initializePhraseeX,
  initializePhraseeXPush,
  initializePhraseeXPushBroadcast,
  RealtimeUpdateTimeUnit,
  updatePhraseeX,
  updatePhraseeXSchedule,
} from './api'
import Button from './Button'
import {
  createAdvancedSettingsPayload,
  isDisabledEndDate,
  submissionError,
} from './helpers'
import {
  AdvancedSettingsPayload,
  OptimizationSettingsConstraints,
} from './types'

const TIME_FORMAT = 'HH:mm'
const PHRASEEX_ENDPOINT = env.phraseexEndpoint
const START_TIME_ADDED_MINUTES = 30

export const getIsoDate = (date: Date | null, time?: string | null): string => {
  if (!date) {
    return ''
  }
  const [hours, minutes] = time?.split(':') || ['0', '0']
  const combined = new Date(date)
  combined.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0)
  return combined.toISOString()
}

// For SFMC React, the recurring schedule should run between 1am and 4am UTC
export const generateSFMCReactRecurringSchedule = (): string => {
  const d = addDays(new Date(), 1)
  d.setUTCHours(Math.floor(Math.random() * 4 + 1), 0, 0, 0)
  return d.toISOString()
}

// When it come to engage experiments we first want the scheduler to run 45 min after the start date, and every hour.
// That's what the two next method are intended for
export const generateSFMCReactRecurringScheduleForBroadcast = (): string => {
  const d = addMinutes(new Date(), 60)
  d.setUTCSeconds(0, 0)
  d.setUTCMinutes(Math.floor(Math.random() * 12) * 5)
  return d.toISOString()
}
export const generateSFMCReactFirstSchedulerTickForBroadcast = (
  startDateTime: string
): string => {
  const d = addMinutes(new Date(startDateTime), 45)
  d.setSeconds(0, 0)
  return d.toISOString()
}

const MIDLIST_ERROR_CASE = {
  emptyMid: `Please reach out to your customer services representative \n
    to configure the Salesforce Integration.`,
  httpFailed: `Failed to fetch MID (Business Unit) information, \n
  please refresh the page and try again`,
}

type Props = {
  onSuccess?: () => void
}

const PhraseeXIntegration = ({ onSuccess }: Props) => {
  const dispatch = useAppDispatch()
  const flags = useFlags()
  const [error, setError] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isScheduled, setIsScheduled] = useState<boolean>(false)
  const [isUrlCopied, setIsUrlCopied] = useState<boolean>(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [startTime, setStartTime] = useState<string | null>()
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [endTime, setEndTime] = useState<string | null>()
  const [isDynamicOptimizationPaused, setIsDynamicOptimizationPaused] =
    useState<boolean>(false)
  const [frequency, setFrequency] = useState<
    RealtimeUpdateTimeUnit | undefined
  >()
  // Flag which determines when to show "Update Settings" button.
  const [areAdvancedSettingsUpdateable, setAreAdvancedSettingsUpdateable] =
    useState(false)
  const [originalBanditData, setOriginalBanditData] = useState<
    AdvancedSettingsPayload | undefined
  >(undefined)
  const [mid, setMid] = useState<string>('')
  const [midListError, setMidListError] = useState<string>('')
  const [hasMadeMidCall, setHasMadeMidCall] = useState<boolean>(false)
  const projectId = useAppSelector((state) => state.campaignStates.projectId)

  const campaignConfiguration = useAppSelector(
    (state) => state.campaignStates?.campaignData?.campaign_configuration
  )
  const testTrackedMetrics = campaignConfiguration?.test_tracked_metrics as
    | { name: string; type: string }[]
    | undefined

  const hasTrackedClickMetrics = testTrackedMetrics?.some((metric) =>
    metric?.name.includes('click')
  )

  const testedContentSections = campaignConfiguration?.testing_method
    ?.tested_content_sections as
    | {
        section: string
        language_source: string
      }[]
    | undefined

  const campaignsTestedContentSection = testedContentSections?.[0].section as
    | TestedContentSection
    | undefined

  const isInBody = campaignsTestedContentSection === 'in_body'

  const campaignDistributionType = useAppSelector(
    (state) =>
      state.campaignStates?.campaignData?.campaign_configuration
        ?.distribution_type
  )

  const selectionMetric = campaignConfiguration?.testing_method
    ?.optimization_method?.selection_metric?.name as string | undefined

  const [advancedSettingsState, dispatchAdvancedSettingsState] = useReducer(
    advancedSettingsReducer,
    initialAdvancedOptionsState({
      selectionMetric,
      testedContentSection: campaignsTestedContentSection,
      isInBody,
      hasTrackedClickMetrics,
      isBroadcastCampaign: campaignDistributionType === 'broadcast',
      showDOAutomaticOptions: flags.showDoAutomaticVariantIntroduction,
    })
  )

  const campaignSchedules = useAppSelector(
    (state) =>
      state.campaignStates.campaignData.campaign_data?.bandit_data?.schedules ||
      []
  )
  const isCampaignScheduledAlready = campaignSchedules.length > 0
  const midList = useAppSelector((state) => state.campaignStates?.midList || [])
  const projectIdLinkedToMid = useAppSelector(
    (state) => state.campaignStates.projectIdLinkedToMid
  )

  const isLoadingMid = useAppSelector(
    (state) =>
      state.campaignStates.isWaitingState.isWaiting &&
      state.campaignStates.isWaitingState.isWaitingFor === GET_MID_LOADING
  )
  const campaignMid = useAppSelector(
    (state) =>
      state.campaignStates.campaignData.campaign_data?.bandit_data?.client_id
  )
  const appMidListError = useAppSelector(
    (state) => state.campaignStates?.midListError
  )

  const campaignId = useAppSelector(
    (state) => state.campaignStates.campaignData._id as string
  )

  const { phrasee_x_initialised: isPhraseeXInitialised } = useAppSelector(
    (state) => state.campaignStates.campaignData.campaign_progress
  )

  const { campaignTokenEnabled, campaignToken } = useAppSelector((state) => ({
    campaignTokenEnabled:
      state.campaignStates.campaignData.campaign_configuration
        ?.campaign_token_enabled,
    campaignToken:
      state.campaignStates.campaignData.campaign_data?.campaign_token,
  }))

  const isCachingEnabled: boolean = useAppSelector(
    (state) =>
      !!state.campaignStates.campaignData.campaign_configuration
        ?.realtime_api_settings?.caching
  )

  const campaignData = useAppSelector(
    (state) => state.campaignStates?.campaignData?.campaign_data
  )

  const isRealTimeApiEnabled = useAppSelector(
    (state) =>
      state.campaignStates?.campaignData?.campaign_configuration
        ?.realtime_api_enabled
  )

  const isASaleforcePhraseeXIntegration = useAppSelector((state) => {
    return (
      state.campaignStates?.campaignData?.campaign_configuration
        ?.integration_options?.type === 'sfmcv2' &&
      state.campaignStates?.campaignData?.campaign_configuration?.testing_method
        ?.optimization_method.selection_type === 'bandit'
    )
  })

  const isASaleforcePushPhraseeXIntegration = useAppSelector((state) => {
    return (
      state.campaignStates?.campaignData?.campaign_configuration
        ?.integration_options?.type === 'sfmcv2' &&
      state.campaignStates?.campaignData?.campaign_configuration?.testing_method
        ?.optimization_method.selection_type === 'bandit' &&
      state.campaignStates?.campaignData?.campaign_configuration
        ?.distribution_channel === 'push_notification'
    )
  })

  const shouldShowTestMode = helpers.shouldShowTestMode(campaignConfiguration)

  const productType = useAppSelector(
    (state) =>
      state.campaignStates.campaignData.campaign_configuration?.product_type
  )

  const typeOfIntegration = useAppSelector(
    (state) =>
      state.campaignStates?.campaignData?.campaign_configuration
        ?.integration_options?.type
  )

  const shouldDisplayRealTimeApiUrl =
    isRealTimeApiEnabled && typeOfIntegration !== 'cordial'

  const frequencies: { name: string; value: RealtimeUpdateTimeUnit }[] = [
    ...(campaignDistributionType === 'broadcast'
      ? [{ name: 'Once', value: 'minutes' as RealtimeUpdateTimeUnit }]
      : []),
    { name: 'Daily', value: 'days' },
    { name: 'Weekly', value: 'weeks' },
    { name: 'Monthly', value: 'months' },
  ]

  const shouldSaveSchedule =
    typeOfIntegration !== 'adobe_campaign_classic' && isRealTimeApiEnabled

  const hasIntegrationConfiguration = !!useAppSelector(
    (state) =>
      state.campaignStates?.campaignData?.campaign_configuration
        ?.integration_options?.configuration
  )

  const getPhraseeXEndpoint = () => {
    const baseUrl = `${PHRASEEX_ENDPOINT}?campaign_id=${campaignId}`
    let endpoint = baseUrl
    if (typeOfIntegration === 'iterable') {
      endpoint += `&recipient_id={{#urlEncode}}{{email}}{{/urlEncode}}&delivery_id={{campaignId}}`
    } else if (
      isCachingEnabled ||
      helpers.hasClicksUnsubsConversionsAsDataSyncSource(
        campaignConfiguration
      ) ||
      typeOfIntegration === 'moengage'
    ) {
      endpoint += `&recipient_id=<unique customer ID>&delivery_id=<unique delivery ID>`
    }
    if (campaignTokenEnabled && campaignToken) {
      endpoint += `&campaign_token=${campaignToken}`
    }
    return endpoint
  }

  const phraseeXEndpoint = getPhraseeXEndpoint()

  const scheduleHandler = async (isUpdating: boolean) => {
    const startDateTime = getIsoDate(startDate, startTime)
    const endDateTime = getIsoDate(endDate, endTime)

    const { optimization_start_time } = campaignData || {}

    const submissionErrorText = submissionError(
      optimization_start_time,
      startDateTime,
      endDateTime,
      campaignDistributionType
    )
    if (submissionErrorText) {
      setError(submissionErrorText)
      return
    }

    try {
      const payload = {
        campaign_id: campaignId,
        optimization_start_time: startDateTime,
        optimization_end_time: endDateTime,
        campaign_start_time: startDateTime,
        campaign_end_time: endDateTime,
        dynamic_optimization_paused: isDynamicOptimizationPaused,
        ...createAdvancedSettingsPayload(
          advancedSettingsState,
          campaignDistributionType,
          flags.showDoAutomaticVariantIntroduction
        ),
      }

      const PhraseeXSchedulePayload = {
        optimization_start_time: startDateTime,
        optimization_end_time: endDateTime,
      }

      setIsLoading(true)
      setError(undefined)
      if (!isPhraseeXInitialised) {
        await initializePhraseeX(campaignId, payload)
        onSuccess?.()
      } else if (isUpdating) {
        await updatePhraseeX(campaignId, payload)
        if (shouldSaveSchedule) {
          await updatePhraseeXSchedule(campaignId, {
            ...PhraseeXSchedulePayload,
            realtime_update_interval: frequency === 'minutes' ? 15 : 1,
            realtime_update_time_unit: frequency!,
          })
        }
      }

      // this fetches campaignData after user scheduled new campaign, so when user closed integration drawer and reopened it again - drawer displays  newly created campaign and is not empty
      dispatch(fetchCampaignData(campaignId))

      setIsLoading(false)
      setIsScheduled(true)
    } catch (err: any) {
      setError(err?.response?.data?.name || err?.message)
      setIsLoading(false)
    }
  }

  const scheduleSFMCPhraseeX = async () => {
    setIsLoading(true)
    setError(undefined)
    const startDateTime = getIsoDate(startDate, startTime)
    const endDateTime = getIsoDate(endDate, endTime)

    const { optimization_start_time } = campaignData || {}
    const submissionErrorText = submissionError(
      optimization_start_time,
      startDateTime,
      endDateTime,
      campaignDistributionType
    )
    if (submissionErrorText) {
      setError(submissionErrorText)
      return
    }

    const payloadData = {
      campaign_id: campaignId,
      dynamic_optimization_paused: isDynamicOptimizationPaused,
      schedules:
        campaignDistributionType === 'broadcast'
          ? [
              {
                recurring: true,
                interval_type: 'hours',
                interval_value: 1,
                start_utc_iso_date_time: startDateTime,
                end_utc_iso_date_time: endDateTime,
                utc_iso_date_time:
                  generateSFMCReactRecurringScheduleForBroadcast(),
              },
              {
                utc_iso_date_time:
                  generateSFMCReactFirstSchedulerTickForBroadcast(
                    startDateTime
                  ),
              },
              {
                utc_iso_date_time: subMinutes(
                  new Date(startDateTime),
                  15
                ).toISOString(),
              },
            ]
          : [
              {
                recurring: true,
                interval_type: 'days',
                interval_value: 1,
                start_utc_iso_date_time: startDateTime,
                end_utc_iso_date_time: endDateTime,
                utc_iso_date_time: generateSFMCReactRecurringSchedule(),
              },
              {
                utc_iso_date_time: subMinutes(
                  new Date(startDateTime),
                  30
                ).toISOString(),
              },
            ],
      ...(campaignDistributionType === 'broadcast' && {
        is_sfmc_engage: true,
      }),
      interval_value: campaignDistributionType === 'broadcast' ? 45 : 1,
      interval_type: frequency!,
      optimization_start_time: startDateTime,
      optimization_end_time: endDateTime,
      campaign_start_time: startDateTime,
      campaign_end_time: endDateTime,
      client_id: mid,
      ...createAdvancedSettingsPayload(
        advancedSettingsState,
        campaignDistributionType,
        flags.showDoAutomaticVariantIntroduction
      ),
    }

    if (!isPhraseeXInitialised) {
      try {
        if (
          isASaleforcePushPhraseeXIntegration &&
          campaignDistributionType === 'broadcast'
        ) {
          await initializePhraseeXPushBroadcast(payloadData)
        } else if (isASaleforcePushPhraseeXIntegration) {
          await initializePhraseeXPush(payloadData)
        } else {
          await initializePhraseeX(campaignId, payloadData)
        }
        dispatch(loadResultsDetails(campaignId))
        onSuccess?.()
      } catch (err: any) {
        setError(err?.response?.data?.name || err?.message)
        setIsLoading(false)
        return
      }
    } else {
      try {
        await updatePhraseeX(campaignId, payloadData)
      } catch (err: any) {
        setError(err?.response?.data?.name || err?.message)
        setIsLoading(false)
        return
      }
    }
    dispatch(fetchCampaignData(campaignId))
    setIsLoading(false)
  }

  useEffect(() => {
    if (campaignData?.hasOwnProperty('bandit_data')) {
      setIsScheduled(true)
      const { optimization_start_time, optimization_end_time, bandit_data } =
        campaignData
      const {
        allow_control_drop,
        drop_bad_variants,
        interval_type,
        optimization_settings,
        performance_metric,
        dynamic_optimization_paused,
        language_controller,
        optimization_metric,
        optimization_audience,
      } = bandit_data

      setIsDynamicOptimizationPaused(!!dynamic_optimization_paused)
      optimization_start_time && setStartDate(new Date(optimization_start_time))
      optimization_end_time && setEndDate(new Date(optimization_end_time))
      optimization_start_time &&
        setStartTime(format(new Date(optimization_start_time), 'HH:mm'))
      optimization_end_time &&
        setEndTime(format(new Date(optimization_end_time), 'HH:mm'))
      const frequency = frequencies.find((f) => f.value === interval_type)
      setFrequency(
        (interval_type as RealtimeUpdateTimeUnit) ||
          (frequency?.value as RealtimeUpdateTimeUnit) ||
          ('days' as RealtimeUpdateTimeUnit)
      )

      drop_bad_variants !== undefined &&
        dispatchAdvancedSettingsState({
          type: 'set_is_drop_bad_variants_enabled',
          value: drop_bad_variants,
        })
      allow_control_drop !== undefined &&
        dispatchAdvancedSettingsState({
          type: 'set_is_drop_human_control_enabled',
          value: allow_control_drop,
        })
      performance_metric &&
        dispatchAdvancedSettingsState({
          type: 'set_optimization_metric',
          value: hasIntegrationConfiguration ? 'generic' : performance_metric,
        })
      optimization_metric &&
        optimization_audience &&
        dispatchAdvancedSettingsState({
          type: 'set_integration_optimization_metrics',
          value: {
            integrationOptimizationAudience: optimization_audience,
            integrationOptimizationMetric: optimization_metric,
          },
        })

      if (optimization_settings) {
        const {
          mode,
          head_to_head_days,
          head_to_head_winner_prop,
          pool_size,
          min_hc_percent,
          test_audience_size,
        } = optimization_settings

        dispatchAdvancedSettingsState({
          type: 'set_optimization_mode',
          value: mode,
        })

        const languageControllerMode = language_controller
          ? language_controller.mode
          : !!head_to_head_days
            ? optionHeadToHeadAndWait.value
            : undefined

        if (flags.showDoAutomaticVariantIntroduction) {
          dispatchAdvancedSettingsState({
            type: 'set_be_smart_when_to_introduce_variants_value',
            value: languageControllerMode,
          })
        } else {
          dispatchAdvancedSettingsState({
            type: 'set_is_days_before_adding_lines_enabled',
            value: !!head_to_head_days,
          })
        }

        dispatchAdvancedSettingsState({
          type: 'set_days_before_adding_lines',
          value: head_to_head_days,
        })

        dispatchAdvancedSettingsState({
          type: 'set_proportion_send_to_winner_number',
          value:
            head_to_head_winner_prop !== null
              ? handlePercentValue('percent', head_to_head_winner_prop)
              : undefined,
        })

        dispatchAdvancedSettingsState({
          type: 'set_high_performers_number',
          value: pool_size,
        })

        dispatchAdvancedSettingsState({
          type: 'set_high_performers_number',
          value: pool_size,
        })
        dispatchAdvancedSettingsState({
          type: 'set_is_min_sends_to_human_control_enabled',
          value: !!min_hc_percent,
          isBroadcastCampaign: campaignDistributionType === 'broadcast',
        })
        dispatchAdvancedSettingsState({
          type: 'set_min_sends_to_human_control_value',
          value:
            min_hc_percent !== undefined
              ? Math.trunc(min_hc_percent * 100)
              : undefined,
        })

        dispatchAdvancedSettingsState({
          type: 'set_test_audience_size_enabled',
          value: !!test_audience_size,
          isBroadcastCampaign: campaignDistributionType === 'broadcast',
        })
        dispatchAdvancedSettingsState({
          type: 'set_test_audience_size_value',
          value:
            test_audience_size !== undefined
              ? Math.trunc(test_audience_size * 100)
              : undefined,
        })

        // Save original bandit data to track user changes in Advanced settings.
        // Each mode has different sets of possible combinations, that's why we don't want to define properties if they are not included.
        const defaultAdvancedSettingsPayload = {
          allow_control_drop,
          drop_bad_variants,
          performance_metric,
          optimization_settings: {
            min_hc_percent:
              min_hc_percent !== undefined
                ? Math.trunc(min_hc_percent * 100)
                : undefined,
            test_audience_size:
              test_audience_size !== undefined
                ? Math.trunc(test_audience_size * 100)
                : undefined,
          },
          ...(flags.showDoAutomaticVariantIntroduction && {
            language_controller: {
              mode: languageControllerMode,
            },
          }),
        }
        let optimizationSettingsModeConfig: OptimizationSettingsConstraints = {
          mode: 'single_best',
          head_to_head_days,
        }
        if (mode === 'random_high_performers') {
          optimizationSettingsModeConfig = {
            mode: 'random_high_performers',
            pool_size,
          }
        } else if (mode === 'confidence') {
          optimizationSettingsModeConfig = {
            mode: 'confidence',
            head_to_head_winner_prop: handlePercentValue(
              'percent',
              head_to_head_winner_prop
            ),
            head_to_head_days,
          }
        }
        let integrationConfigurationMetrics
        if (hasIntegrationConfiguration) {
          integrationConfigurationMetrics = {
            integration_audience_metric: optimization_audience ?? undefined,
            integration_optimization_metric: optimization_metric ?? undefined,
          }
        }
        setOriginalBanditData({
          ...defaultAdvancedSettingsPayload,
          ...(hasIntegrationConfiguration && integrationConfigurationMetrics),
          optimization_settings: {
            ...defaultAdvancedSettingsPayload['optimization_settings'],
            ...optimizationSettingsModeConfig,
          },
        })
      }

      // Backward compatibility, in case a campaign has old data structure for "optimization mode"
      if (bandit_data.optimization_mode) {
        dispatchAdvancedSettingsState({
          type: 'set_optimization_mode',
          value: advancedSettingsState.optimizationMode,
        })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData])

  // for new (not yet initialised) PhraseeX react campaigns dropBadVariants is set to true, for engage campaigns it stays false
  useEffect(() => {
    if (!isPhraseeXInitialised && productType === 'react') {
      dispatchAdvancedSettingsState({
        type: 'set_is_drop_bad_variants_enabled',
        value: true,
      })
    }
  }, [isPhraseeXInitialised, productType])

  // If the campaign distribution type is broadcast we default the frequency to once
  useEffect(() => {
    if (campaignDistributionType === 'broadcast' && !frequency) {
      setFrequency('minutes')
    }
  }, [campaignDistributionType, frequency])
  // Hook to track when "Advanced settings" has changed. Enables update button if data has changed.
  useEffect(() => {
    // Update is possible only when campaign is scheduled and campaign has 'bandit_data'
    if (isScheduled && originalBanditData) {
      let currentUserAdvancedOptions: AdvancedSettingsPayload | undefined =
        undefined

      // Each mode has different sets of possible combinations
      if (advancedSettingsState.optimizationMode === singleBest.value) {
        currentUserAdvancedOptions = {
          allow_control_drop: advancedSettingsState.isDropHumanControlEnabled,
          drop_bad_variants: advancedSettingsState.isDropBadVariantsEnabled,
          performance_metric: advancedSettingsState.optimizationMetric,
          optimization_settings: {
            mode: advancedSettingsState.optimizationMode,
            min_hc_percent:
              advancedSettingsState.minSendsToHumanControlInputValue,
            test_audience_size: advancedSettingsState.testAudienceSizeValue,
            head_to_head_days: advancedSettingsState.daysBeforeAddingLines,
          },
          ...(hasIntegrationConfiguration
            ? {
                integration_optimization_metric:
                  advancedSettingsState.integrationOptimizationMetric,
                integration_audience_metric:
                  advancedSettingsState.integrationOptimizationAudience,
              }
            : {}),
          ...(flags.showDoAutomaticVariantIntroduction && {
            language_controller: {
              mode: advancedSettingsState.beSmartWhenToIntroduceVariants,
            },
          }),
        }
      }
      if (advancedSettingsState.optimizationMode === highPerformers.value) {
        currentUserAdvancedOptions = {
          allow_control_drop: advancedSettingsState.isDropHumanControlEnabled,
          drop_bad_variants: advancedSettingsState.isDropBadVariantsEnabled,
          performance_metric: advancedSettingsState.optimizationMetric,
          optimization_settings: {
            mode: advancedSettingsState.optimizationMode,
            min_hc_percent:
              advancedSettingsState.minSendsToHumanControlInputValue,
            test_audience_size: advancedSettingsState.testAudienceSizeValue,
            pool_size: advancedSettingsState.highPerformersNumber,
          },
          ...(hasIntegrationConfiguration
            ? {
                integration_optimization_metric:
                  advancedSettingsState.integrationOptimizationMetric,
                integration_audience_metric:
                  advancedSettingsState.integrationOptimizationAudience,
              }
            : {}),
          ...(flags.showDoAutomaticVariantIntroduction && {
            language_controller: {
              mode: advancedSettingsState.beSmartWhenToIntroduceVariants,
            },
          }),
        }
      }
      if (advancedSettingsState.optimizationMode === confidence.value) {
        currentUserAdvancedOptions = {
          optimization_settings: {
            mode: advancedSettingsState.optimizationMode,
            min_hc_percent:
              advancedSettingsState.minSendsToHumanControlInputValue,
            test_audience_size: advancedSettingsState.testAudienceSizeValue,
            head_to_head_days: advancedSettingsState.daysBeforeAddingLines,
            head_to_head_winner_prop:
              advancedSettingsState.proportionSendToWinnerNumber,
          },
          allow_control_drop: advancedSettingsState.isDropHumanControlEnabled,
          drop_bad_variants: advancedSettingsState.isDropBadVariantsEnabled,
          performance_metric: advancedSettingsState.optimizationMetric,
          ...(hasIntegrationConfiguration
            ? {
                integration_optimization_metric:
                  advancedSettingsState.integrationOptimizationMetric,
                integration_audience_metric:
                  advancedSettingsState.integrationOptimizationAudience,
              }
            : {}),
          ...(flags.showDoAutomaticVariantIntroduction && {
            language_controller: {
              mode: advancedSettingsState.beSmartWhenToIntroduceVariants,
            },
          }),
        }
      }
      if (!isEqual(originalBanditData, currentUserAdvancedOptions)) {
        setAreAdvancedSettingsUpdateable(true)
      } else {
        setAreAdvancedSettingsUpdateable(false)
      }
    }
  }, [
    advancedSettingsState.daysBeforeAddingLines,
    advancedSettingsState.highPerformersNumber,
    advancedSettingsState.isDropBadVariantsEnabled,
    advancedSettingsState.isDropHumanControlEnabled,
    advancedSettingsState.minSendsToHumanControlInputValue,
    advancedSettingsState.optimizationMetric,
    advancedSettingsState.optimizationMode,
    advancedSettingsState.proportionSendToWinnerNumber,
    advancedSettingsState.testAudienceSizeValue,
    advancedSettingsState.integrationOptimizationMetric,
    advancedSettingsState.integrationOptimizationAudience,
    isScheduled,
    originalBanditData,
    advancedSettingsState.beSmartWhenToIntroduceVariants,
    flags.showDoAutomaticVariantIntroduction,
    hasIntegrationConfiguration,
  ])

  useEffect(() => {
    if (isCampaignScheduledAlready && campaignMid) {
      setMid(campaignMid)
    } else {
      if (midList?.length && !mid) {
        setMid(midList[0].mid)
      }
      if (appMidListError) {
        setMidListError(MIDLIST_ERROR_CASE.httpFailed)
      }
    }
    if (projectIdLinkedToMid && projectIdLinkedToMid !== projectId) {
      setHasMadeMidCall(false)
      setMid('')
      dispatch(resetMidList())
    }
    if (!midList?.length && !appMidListError && !isLoadingMid) {
      if (!hasMadeMidCall) {
        !campaignMid &&
          dispatch(getMID(projectId)).then(() => setHasMadeMidCall(true))
      } else {
        setMidListError(MIDLIST_ERROR_CASE.emptyMid)
      }
    }
  }, [
    dispatch,
    midList,
    projectId,
    appMidListError,
    hasMadeMidCall,
    campaignSchedules,
    campaignMid,
    isCampaignScheduledAlready,
    mid,
    isLoadingMid,
    projectIdLinkedToMid,
  ])

  const disabledStartDate = (current: Date) => {
    return isBefore(current, endOfDay(addDays(new Date(), -1)))
  }
  const disabledEndDate = (current: Date) => {
    return isDisabledEndDate(current, campaignDistributionType, startDate)
  }

  const getDisabledStartHours = (): number[] => []

  const getDisabledStartMinutes = (_selectedHour: number): number[] => []

  const getDisabledEndHours = (): number[] => []

  const getDisabledEndMinutes = (_selectedHour: number): number[] => []

  const checkIsOptimizationUpdating = (campaignData: CampaignData) => {
    const { optimization_start_time, optimization_end_time } =
      campaignData || {}

    const hasStartDateChanged =
      (startDate ? format(startDate, 'yyyy-MM-dd') : '') !==
      (optimization_start_time
        ? format(new Date(optimization_start_time), 'yyyy-MM-dd')
        : '')

    const hasEndDateChanged =
      (endDate ? format(endDate, 'yyyy-MM-dd') : '') !==
      (optimization_end_time
        ? format(new Date(optimization_end_time), 'yyyy-MM-dd')
        : '')

    const hasStartTimeChanged =
      (optimization_start_time
        ? format(new Date(optimization_start_time), TIME_FORMAT)
        : '') !== (startTime ?? '')

    const hasEndTimeChanged =
      (optimization_end_time
        ? format(new Date(optimization_end_time), TIME_FORMAT)
        : '') !== (endTime ?? '')
    return (
      isScheduled &&
      isPhraseeXInitialised &&
      (hasStartDateChanged ||
        hasEndDateChanged ||
        hasStartTimeChanged ||
        hasEndTimeChanged)
    )
  }

  const isTestModeUpdating =
    isDynamicOptimizationPaused !==
    campaignData?.bandit_data?.dynamic_optimization_paused

  const isOptimizationUpdating = checkIsOptimizationUpdating(campaignData)

  const isValidIntegrationConfiguration =
    !hasIntegrationConfiguration ||
    (hasIntegrationConfiguration &&
      advancedSettingsState.integrationOptimizationAudience &&
      advancedSettingsState.integrationOptimizationMetric)

  const areInputsUpdateable =
    (areAdvancedSettingsUpdateable && isValidIntegrationConfiguration) ||
    isOptimizationUpdating ||
    (campaignData?.bandit_data?.dynamic_optimization_paused !== undefined &&
      isTestModeUpdating)

  const isCtaEnabled = !!(
    (startDate &&
      startTime &&
      endDate &&
      endTime &&
      isValidIntegrationConfiguration) ||
    areInputsUpdateable
  )

  const canRescheduleSchedule = (scheduleTime: string, isEndTime: boolean) => {
    if (campaignDistributionType === 'AlwaysOn' && isEndTime) {
      return true
    }

    if (
      isLoading ||
      (isPhraseeXInitialised &&
        !(
          scheduleTime &&
          differenceInSeconds(new Date(scheduleTime), new Date()) > 0
        ))
    ) {
      return false
    }
    return true
  }

  const isExperimentBeingRestarted = () => {
    const endDateTime = getIsoDate(endDate, endTime)
    const now = new Date().toISOString()
    const { optimization_end_time } = campaignData || {}

    if (
      isPhraseeXInitialised &&
      endDateTime > optimization_end_time &&
      optimization_end_time < now
    ) {
      return true
    }
    return false
  }

  const canRescheduleOptimizationStart = canRescheduleSchedule(
    campaignData?.optimization_start_time,
    false
  )

  const canRescheduleOptimizationEnd = canRescheduleSchedule(
    campaignData?.optimization_end_time,
    true
  )

  const getMidDisplayName = (midObject: MidObject) => {
    if (midObject.mid && midObject.name) {
      return `${midObject.mid} - ${midObject.name}`
    } else if (midObject.mid && !midObject.name) {
      return `${midObject.mid}`
    }

    return ''
  }
  const getMidValues = () => {
    const midElement = midList.find((midObject: MidObject) => {
      return mid ? midObject.mid === mid : midObject
    })
    return midElement ? getMidDisplayName(midElement) : `${mid}`
  }

  const testModeDescription = getTestModeDescription(isScheduled, campaignData)

  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // TODO: Use scheduler component
  return (
    <>
      <Drawer.Content>
        <div className="text-coolGray-500 font-medium mb-6 text-base updated-component">
          Schedule your dynamic optimization experiment.
        </div>
        {isASaleforcePhraseeXIntegration && (
          <FormItem label="Salesforce MID">
            {isCampaignScheduledAlready ? (
              <p>{getMidValues()}</p>
            ) : (
              <SingleSelect
                isLoading={isLoadingMid}
                data-testid="selectMid"
                isDisabled={mid?.length <= 1 || campaignMid}
                placeholder="Select a MID"
                options={midList?.map((midObject: MidObject) => ({
                  label: getMidDisplayName(midObject),
                  value: midObject.mid,
                }))}
                value={mid}
                onChange={(value) => value && setMid(value.value)}
              />
            )}
            {midListError && (
              <span className="text-sm font-normal">{midListError}</span>
            )}
          </FormItem>
        )}
        {isScheduled && frequency && shouldShowTestMode && (
          <TestModeAlert
            campaignData={campaignData}
            isPaused={isDynamicOptimizationPaused}
            setIsPaused={setIsDynamicOptimizationPaused}
            testModeDescription={testModeDescription}
          />
        )}
        <OptimizationSettings
          advancedSettingsState={advancedSettingsState}
          dispatchAdvancedSettings={dispatchAdvancedSettingsState}
        />
        <Card className="my-6">
          <div className="flex items-center text-coolGray-400 mb-4">
            <TimeIcon size={6} />
            <span className="text-coolGray-800 font-medium text-base ml-4">
              Optimisation schedule
            </span>
          </div>
          <Label>Start schedule</Label>
          <div className="flex mb-6 gap-4" data-testid="start-schedule">
            <DatePicker
              id="date-picker"
              numberOfMonths={1}
              verticalHeight={372}
              isTodayVisible={true}
              showClearDate={false}
              ariaLabel="startDatePicker"
              disabled={!canRescheduleOptimizationStart}
              isOutsideRange={disabledStartDate}
              date={startDate}
              onDateChange={(date: Date | null) => {
                if (date) {
                  setStartDate(date)
                  setEndDate(addDays(date, 4))
                }
              }}
            />
            <TimePicker
              showSecond={false}
              allowClear={false}
              data-testid="startTimePicker"
              disabled={!canRescheduleOptimizationStart}
              format={TIME_FORMAT}
              minuteStep={15}
              value={startTime ?? null}
              disabledHours={getDisabledStartHours}
              disabledMinutes={getDisabledStartMinutes}
              onChange={(time: string | null) => {
                setStartTime(time ?? null)
              }}
              placeholder="Time"
            />
          </div>
          <Label>End schedule</Label>
          <div className="flex mb-6 gap-4" data-testid="end-schedule">
            <DatePicker
              id="date-picker"
              numberOfMonths={1}
              verticalHeight={372}
              isTodayVisible={true}
              showClearDate={false}
              ariaLabel="endDatePicker"
              disabled={!canRescheduleOptimizationEnd}
              isOutsideRange={disabledEndDate}
              date={endDate}
              onDateChange={(date: Date | null) => {
                if (date) {
                  setEndDate(date)
                }
              }}
            />
            <TimePicker
              showSecond={false}
              allowClear={false}
              data-testid="endTimePicker"
              disabled={!canRescheduleOptimizationEnd}
              format={TIME_FORMAT}
              minuteStep={15}
              value={endTime ?? null}
              onChange={(time: string | null) => {
                setEndTime(time ?? null)
              }}
              placeholder="Time"
              disabledHours={getDisabledEndHours}
              disabledMinutes={getDisabledEndMinutes}
            />
          </div>
          <div className="mb-2 font-medium">
            Detected Timezone: {detectedTimezone}{' '}
            {new Intl.DateTimeFormat('en-US', {
              timeZone: detectedTimezone,
              timeZoneName: 'short',
            })
              .formatToParts(new Date())
              .find((p) => p.type === 'timeZoneName')?.value ?? ''}
          </div>
        </Card>
        <AdvancedOptions
          advancedSettingsState={advancedSettingsState}
          dispatchAdvancedSettings={dispatchAdvancedSettingsState}
        />
        {shouldDisplayRealTimeApiUrl && (
          <>
            <Label>Copy this URL in to your provider</Label>
            <CopyCard
              isCopied={isUrlCopied}
              text={phraseeXEndpoint}
              setIsCopied={setIsUrlCopied}
              data-testid="optimisation-endpoint-copycard"
            />
            <GuidingText
              className="mt-3"
              text="This allows your provider to connect to the correct experiment within Jacquard."
            />
          </>
        )}
      </Drawer.Content>
      <Drawer.Footer error={error}>
        <Button
          isDisabled={!isCtaEnabled}
          isUpdateable={areInputsUpdateable}
          isLoading={isLoading}
          isScheduled={isScheduled}
          scheduleHandler={
            isASaleforcePhraseeXIntegration
              ? scheduleSFMCPhraseeX
              : scheduleHandler
          }
          isExperimentBeingRestarted={isExperimentBeingRestarted()}
        />
      </Drawer.Footer>
    </>
  )
}

export default PhraseeXIntegration
